import { AxiosRequestConfig } from 'axios';
import api from '../api';
import { getApiBaseURL } from '../apiConfig';

/**
 * Repository base que gerencia autenticação e requisições HTTP
 * Todas as chamadas de API devem passar por este repositório
 * Usa a instância compartilhada do api.ts que já tem os interceptors configurados
 * 
 * NOTA: Tokens são gerenciados via cookies HttpOnly, então não podemos
 * ler/validar tokens diretamente. A autenticação é feita automaticamente
 * via cookies enviados pelo browser.
 */
export abstract class BaseRepository {
  protected api = api; // Usa a instância compartilhada do api.ts
  protected baseURL: string;

  constructor(baseURL?: string) {
    this.baseURL = baseURL || getApiBaseURL();
    // Não precisa criar nova instância, usa a compartilhada que já tem interceptors
  }

  // Interceptors já estão configurados no api.ts compartilhado
  // Não precisa configurar novamente aqui

  /**
   * Verifica se o endpoint é público (não requer autenticação)
   */
  protected isPublicEndpoint(url: string, method: string): boolean {
    // Apenas POST /empresas é público
    return url === '/empresas' && method === 'POST';
  }

  /**
   * Método genérico para GET requests
   * O token é adicionado automaticamente pelo interceptor do api.ts
   */
  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(url, config);
    return response.data;
  }

  /**
   * Método genérico para POST requests
   * O token é adicionado automaticamente pelo interceptor do api.ts
   */
  protected async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, config);
    return response.data;
  }

  /**
   * Método genérico para PATCH requests
   * O token é adicionado automaticamente pelo interceptor do api.ts
   */
  protected async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch<T>(url, data, config);
    return response.data;
  }

  /**
   * Método genérico para PUT requests
   * O token é adicionado automaticamente pelo interceptor do api.ts
   */
  protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  /**
   * Método genérico para DELETE requests
   * O token é adicionado automaticamente pelo interceptor do api.ts
   */
  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(url, config);
    return response.data;
  }
}

